import React, { useState, useEffect, useRef } from 'react';
import styles from './CosyStays.module.css';
import YouMayAlsoLike from '../components/YouMayAlsoLike';

const CosyStays = ({ onNavigate }) => {
  const [activeRoom, setActiveRoom] = useState('rosie');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const pageRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Room data with placeholder content
  const rooms = {
    rosie: {
      name: 'King Room with Exceptional Castle View',
      tabName: 'Room 1',
      bedType: 'King Bed',
      view: 'Castle view',
      stationView: true,
      gardenView: true,
      maxOccupancy: 'Max:2',
      dogFriendly: true,
      amenities: [
        'En-suite bathroom',
        'Shower',
        'Kettle',
        'Treat jar',
        'Free Wi-Fi',
        'Flat-screen TV',
        'Towels',
        'Heating',
        'Hairdryer',
        'Bottled water'
      ],
      importantNotes: [
        'No Smoking',
        'Upper floor room, access via stairs only',
        'Close to railway line'
      ],
      images: [
        { src: '/website_images/rosie_room1.jpg', alt: 'Rosie Room View 1' },
        { src: '/website_images/rosie_room2.jpg', alt: 'Rosie Room View 2' },
        { src: '/website_images/rosie_room3.jpg', alt: 'Rosie Room View 3' },
        { src: '/website_images/rosie_bath1.jpg', alt: 'Rosie Room Bathroom View 1' },
        { src: '/website_images/rosie_bath2.jpg', alt: 'Rosie Room Bathroom View 2' }
      ],
      description: 'Our premium room with spectacular castle views and luxury amenities. Perfect for a special occasion.'
    },
    reggie: {
      name: 'Double Room - No Pets',
      tabName: 'Room 5',
      bedType: 'Double Bed',
      view: 'Mountain View',
      maxOccupancy: 'Max:2',
      dogFriendly: false,
      amenities: [
        'En-suite bathroom',
        'Shower',
        'Kettle',
        'Treat jar',
        'Free Wi-Fi',
        'Flat-screen TV',
        'Towels',
        'Heating',
        'Hairdryer',
        'Bottled water'
      ],
      importantNotes: [
        'Pet-free room',
        'No Smoking',
        'Upper floor room, access via stairs only',
        'Compact bathroom'
      ],
      images: [
        { src: '/website_images/reggie_room1.jpg', alt: 'Reggie Room View 1' },
        { src: '/website_images/reggie_room2.jpg', alt: 'Reggie Room View 2' },
        { src: '/website_images/reggie_room3.jpg', alt: 'Reggie Room View 3' },
        { src: '/website_images/reggie_room4.jpg', alt: 'Reggie Room View 4' },
        { src: '/website_images/reggie_room5.jpg', alt: 'Reggie Room View 5' },
        { src: '/website_images/reggie_bath1.jpg', alt: 'Reggie Room Bathroom View' }
      ],
      description: 'A comfortable double room offering stunning mountain views. Perfect for couples seeking a peaceful retreat with modern amenities and breathtaking scenery. The room provides excellent value while maintaining high standards of comfort and cleanliness.'
    },
    rocco: {
      name: 'Double/Twin Room',
      tabName: 'Room 4',
      bedType: 'Double Bed & Single Bed',
      view: 'Mountain View',
      maxOccupancy: 'Max:2',
      dogFriendly: true,
      amenities: [
        'En-suite bathroom',
        'Shower/Bath',
        'Kettle',
        'Treat jar',
        'Free Wi-Fi',
        'Flat-screen TV',
        'Hairdryer',
        'Towels',
        'Bottled water'
      ],
      importantNotes: [
        'No Smoking',
        'Upper floor room, access via stairs only',
        'Close to road'
      ],
      images: [
        { src: '/website_images/rocco_room1.jpg', alt: 'Rocco Room View 1' },
        { src: '/website_images/rocco_room2.jpg', alt: 'Rocco Room View 2' },
        { src: '/website_images/rocco_room3.jpg', alt: 'Rocco Room View 3' },
        { src: '/website_images/rocco_room4.jpg', alt: 'Rocco Room View 4' },
        { src: '/website_images/rocco_bath.jpg', alt: 'Rocco Room Bathroom View' }
      ],
      description: 'A spacious deluxe room featuring flexible sleeping arrangements with both a double bed and single bed, perfect for families or groups of up to three guests. Enjoy stunning mountain views from this comfortable accommodation with premium amenities and thoughtful design elements.'
    },
    roxy: {
      name: 'Double/Twin Room - No Pets',
      tabName: 'Room 6',
      bedType: 'Double Bed & Single Bed',
      view: 'Mountain View',
      maxOccupancy: 'Max:2',
      dogFriendly: false,
      amenities: [
        'En-suite bathroom',
        'Shower',
        'Kettle',
        'Free Wi-Fi',
        'Flat-screen TV',
        'Towels',
        'Hairdryer',
        'Bottled water',
        'Treat jar',
        'Armchair'
      ],
      importantNotes: [
        'Pet-free Room',
        'No Smoking',
        'Upper floor room, access via stairs only',
        'Close to road'
      ],
      images: [
        { src: '/website_images/roxy_room1.jpg', alt: 'Roxy Room View 1' },
        { src: '/website_images/roxy_room2.jpg', alt: 'Roxy Room View 2' },
        { src: '/website_images/roxy_room3.jpg', alt: 'Roxy Room View 3' },
        { src: '/website_images/roxy_room4.jpg', alt: 'Roxy Room View 4' },
        { src: '/website_images/roxy_room5.jpg', alt: 'Roxy Room View 5' },
        { src: '/website_images/roxy_bath1.jpg', alt: 'Roxy Room Bathroom View 1' },
        { src: '/website_images/roxy_bath2.jpg', alt: 'Roxy Room Bathroom View 2' }
      ],
      description: 'A spacious triple room featuring both a double bed and single bed, perfect for small families or groups of friends. Enjoy stunning mountain views while relaxing in this comfortable accommodation with modern amenities and ample space for up to three guests.'
    }
  };

  const currentRoom = rooms[activeRoom];

  const handleRoomChange = (roomId) => {
    setActiveRoom(roomId);
    setCurrentImageIndex(0); // Reset image carousel when switching rooms
  };

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === currentRoom.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? currentRoom.images.length - 1 : prevIndex - 1
    );
  };

  return (
    <div ref={pageRef} className={styles['cosy-stays-page']}>
      <div className={styles.container}>
        <h1 className={styles['page-title']}>Cosy Stays</h1>
        
        {/* Introduction Section */}
        <div className={styles['intro-section']}>
          <p>
            Welcome to our comfortable accommodation at The Queens Hotel Harlech. 
            Each of our 4 unique rooms offers modern amenities while maintaining the charm and character of our historic hotel.
          </p>
          <p>
            Whether you're seeking historic castle vistas or mountain views, 
            we have the perfect room for your stay in Harlech. Many of our rooms are dog-friendly, 
            so your four-legged companions can join you on your Welsh adventure.
          </p>
        </div>

        {/* Room Tabs */}
        <div className={styles['room-tabs']}>
          {Object.entries(rooms)
            .sort(([, roomA], [, roomB]) => {
              const roomNumA = parseInt(roomA.tabName.replace('Room ', ''));
              const roomNumB = parseInt(roomB.tabName.replace('Room ', ''));
              return roomNumA - roomNumB;
            })
            .map(([roomId, room]) => (
            <button
              key={roomId}
              className={`${styles['room-tab']} ${activeRoom === roomId ? styles.active : ''}`}
              onClick={() => handleRoomChange(roomId)}
            >
              {room.tabName || room.name}
            </button>
          ))}
        </div>

        {/* Room Content */}
        <div className={styles['room-content']}>
          {/* Room Images Gallery */}
          <div className={styles['room-gallery']} data-testid="room-carousel">
            <div className={styles['main-image-container']}>
              <button className={styles['nav-btn']} onClick={prevImage} aria-label="Previous image">
                ‹
              </button>
              <img 
                src={currentRoom.images[currentImageIndex].src}
                alt={currentRoom.images[currentImageIndex].alt}
                className={styles['main-image']}
              />
              <button className={styles['nav-btn']} onClick={nextImage} aria-label="Next image">
                ›
              </button>
            </div>
            <div className={styles['thumbnail-grid']}>
              {currentRoom.images.map((image, index) => (
                <img
                  key={index}
                  src={image.src}
                  alt={image.alt}
                  className={`${styles.thumbnail} ${index === currentImageIndex ? styles.active : ''}`}
                  onClick={() => selectImage(index)}
                />
              ))}
            </div>
          </div>

          {/* Room Details */}
          <div className={styles['room-details']}>
            <div className={styles['room-header']}>
              <h3 className={styles['room-name']}>{currentRoom.name}</h3>
              <div className={styles['room-specs']}>
                <span className={styles['bed-type']}>{currentRoom.bedType}</span>
                <span className={styles['room-view']}>{currentRoom.view}</span>
                <span className={styles['room-occupancy']}>{currentRoom.maxOccupancy}</span>
                {currentRoom.stationView && <span className={styles['room-view']}>Station View</span>}
                {currentRoom.gardenView && <span className={styles['room-view']}>Garden View</span>}
                {currentRoom.size && <span className={styles['room-size']}>{currentRoom.size}</span>}
                <span className={`${styles['dog-status']} ${currentRoom.dogFriendly ? styles['dog-friendly'] : styles['no-dogs']}`}>
                  {currentRoom.dogFriendly ? 'Dog-friendly' : 'No pets'}
                </span>
              </div>
            </div>

            <p className={styles['room-description']}>{currentRoom.description}</p>

            {/* Amenities */}
            <div className={styles['amenities-section']}>
              <h4>Room Amenities</h4>
              <ul className={styles['amenities-list']}>
                {currentRoom.amenities.map((amenity, index) => (
                  <li key={index} className={styles['amenity-item']}>
                    {amenity}
                  </li>
                ))}
              </ul>
            </div>

            {/* Important Notes */}
            {currentRoom.importantNotes && (
              <div className={styles['important-notes-section']}>
                <h4>Important Notes</h4>
                <ul className={styles['important-notes-list']}>
                  {currentRoom.importantNotes.map((note, index) => (
                    <li key={index} className={styles['important-note-item']}>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Booking Button */}
            <div className={styles['booking-section']}>
              <a 
                href="https://www.booking.com/hotel/gb/the-queens-harlech.en-gb.html"
                target="_blank"
                rel="noopener noreferrer"
                className={styles['booking-button']}
              >
                Book this room
              </a>
            </div>
          </div>
        </div>
      </div>
      <YouMayAlsoLike currentPage="cosy-stays" onNavigate={onNavigate} />
    </div>
  );
};

export default CosyStays;